import { BaseFieldStrategy } from './base-field.strategy';
import { FieldType, Field } from '../models/field.model';
import { RepeatableKeywordFieldConfig } from '../models/field-type.model';
import { EditorComponent, EditorComponentType } from '../models/editor-component.model';
import { KeywordLookupItem } from '../../../../api/lookup/keyword-lookup-item';
import { KeywordFieldStrategy } from './keyword-field.strategy';

export class RepeatableKeywordFieldStrategy extends BaseFieldStrategy<RepeatableKeywordFieldConfig> {
  private readonly keywordFieldStrategy = new KeywordFieldStrategy();

  constructor() {
    super(FieldType.REPEATABLE_KEYWORD_FIELD, 'Repeatable Keyword Field', 'pi pi-list');
  }

  override getDefaultConfig(): RepeatableKeywordFieldConfig {
    return {
      type: FieldType.REPEATABLE_KEYWORD_FIELD,
      label: 'Repeatable Keyword Field',
      required: false,
      readOnly: false,
      key: '',
      maxRepeatCount: 10, // Default maximum 10 repetitions
      subFields: [], // Will be populated from KeywordLookupItem.fields
      columnLayout: 2, // Default to 2 column layout
      enableItemSelection: false, // Default to no item selection
      selectionFieldKey: null, // Default to no selection field
      createdSubFields: [], // Initialize empty array for created sub-fields
      isDropdown: false,
      combinationDisplayNames: [],
    };
  }

  override createField(id: string, keywordData?: KeywordLookupItem): Field {
    const config = this.getDefaultConfig();

    if (keywordData) {
      config.key = keywordData.key;
      config.label = keywordData.displayName ?? keywordData.key;
      config.required = Boolean(keywordData.required);
      config.readOnly = !(keywordData.isWritable);
      config.subFields = (keywordData.fields || []).map(f => this.sanitizeSubFieldLookupItem(f));
      config.selectionFieldKey = keywordData.selectionField ?? null;
      config.enableItemSelection = keywordData.selectable ?? false;

      const lookupDropdown = this.extractLookupDropdownSettings(keywordData);
      config.isDropdown = lookupDropdown.isDropdown;
      config.combinationDisplayNames = lookupDropdown.combinationDisplayNames;
    }

    const field = {
      ...config,
      id,
      key: config.key || `repeatable_field_${id}`,
      value: null, // Value will be an array of sub-field groups
      required: config.required ?? false,
      readOnly: config.readOnly ?? false,
      originalRequired: keywordData?.required ?? false,
      originalIsWritable: keywordData?.isWritable ?? true,
      defaultValue: null, // Default value can be set as needed
      contextType: keywordData?.contextType,
      isDropdown: config.isDropdown ?? false,
      combinationDisplayNames: config.combinationDisplayNames ?? [],
    };

    // Create sub-fields for display using KeywordFieldStrategy
    const allSubFields = this.createSubFields(id, config.subFields);
    // Filter out the selection field from the display
    field.createdSubFields = allSubFields
      .filter(subField => subField.key !== config.selectionFieldKey)
      .map(subField => this.stripLookupConfigFromField(subField));

    return field;
  }

  /** Lookup dropdown config lives on the repeatable field, not on sub-fields. */
  private extractLookupDropdownSettings(keywordData: KeywordLookupItem): { isDropdown: boolean; combinationDisplayNames: string[] } {
    const subFields = keywordData.fields || [];
    const isDropdown = !!(keywordData.isDropdown || subFields.some(f => f.isDropdown));

    const combinationDisplayNames =
      keywordData.combinationDisplayNames?.length
        ? keywordData.combinationDisplayNames
        : (subFields.find(f => f.isDropdown && f.combinationDisplayNames?.length)?.combinationDisplayNames
          ?? subFields.find(f => f.combinationDisplayNames?.length)?.combinationDisplayNames
          ?? []);

    return { isDropdown, combinationDisplayNames };
  }

  private sanitizeSubFieldLookupItem(item: KeywordLookupItem): KeywordLookupItem {
    const { isLookupList, isDropdown, combinationDisplayNames, ...rest } = item;
    return {
      ...rest,
      fields: item.fields?.map(f => this.sanitizeSubFieldLookupItem(f)),
    };
  }

  private stripLookupConfigFromField(subField: Field): Field {
    const { isDropdown, combinationDisplayNames, isLookupList, ...rest } = subField as Field & {
      isDropdown?: boolean;
      combinationDisplayNames?: string[];
      isLookupList?: boolean;
    };
    return rest as Field;
  }

  /**
   * Create Field objects from KeywordLookupItem sub-fields using KeywordFieldStrategy
   */
  createSubFields(parentId: string, subFields: KeywordLookupItem[]): Field[] {
    return subFields.map((subField, index) => {
      const subFieldId = `${parentId}_sub_${index}_${subField.key}`;
      return this.keywordFieldStrategy.createField(subFieldId, subField, true);
    });
  }

  override getEditorConfig(): EditorComponent[] {
    return [
      { type: EditorComponentType.TEXT, key: 'label', label: 'Label', columnIndex: 0 },
      { type: EditorComponentType.NUMBER, key: 'maxRepeatCount', label: 'Max Repeat Count', columnIndex: 0 },
      {
        type: EditorComponentType.SELECT,
        key: 'columnLayout',
        label: 'Column Layout',
        options: [
          { label: '1', value: 1 },
          { label: '2', value: 2 },
          { label: '3', value: 3 }
        ],
        defaultValue: 2,
        columnIndex: 0
      },
      { type: EditorComponentType.CHECKBOX, key: 'readOnly', label: 'Read Only', columnIndex: 0 },
      { type: EditorComponentType.LABEL, key: 'key', label: 'Mapped Keyword', columnIndex: 0 },
      {
        type: EditorComponentType.OBJECT_ORDER_LIST,
        key: 'createdSubFields',
        label: 'Sub Fields Order',
        columnIndex: 1,
        orderListConfig: {
          displayProperty: 'label',
          allowReorder: true
        },
      }
    ];
  }

  override validateValue(value: any, config: RepeatableKeywordFieldConfig): boolean {
    // If no value provided, it's valid (unless required)
    if (value === null || value === undefined) {
      return !config.required;
    }

    // Value should be an array for repeatable fields
    if (!Array.isArray(value)) {
      return false;
    }

    // Check if array length is within limits
    if (value.length > config.maxRepeatCount) {
      return false;
    }

    // Minimum is always 1 (as per requirements)
    if (config.required && value.length < 1) {
      return false;
    }

    // Validate each item in the array
    // Each item should be an object containing values for all sub-fields
    for (const item of value) {
      if (typeof item !== 'object' || item === null) {
        return false;
      }

      // Validate each sub-field in the item
      for (const subField of config.subFields) {
        const subFieldValue = item[subField.key];

        // Check if required sub-field is present
        if (subField.required && (subFieldValue === null || subFieldValue === undefined || subFieldValue === '')) {
          return false;
        }

        // Validate sub-field value based on its type
        if (subFieldValue !== null && subFieldValue !== undefined) {
          if (!this.validateSubFieldValue(subFieldValue, subField)) {
            return false;
          }
        }
      }
    }

    return true;
  }

  /**
   * Validate individual sub-field value based on its data type
   */
  private validateSubFieldValue(value: any, subField: KeywordLookupItem): boolean {
    switch (subField.uiType) {
      case 'Text':
      case 'Notes':
      case 'Country':
        return typeof value === 'string';

      case 'Integer':
        return typeof value === 'number' && Number.isInteger(value);

      case 'Decimal':
        return typeof value === 'number' && !Number.isNaN(value);

      case 'Boolean':
        return typeof value === 'boolean';

      case 'Date Only':
        if (value instanceof Date) {
          return !Number.isNaN(value.getTime());
        }
        if (typeof value === 'string') {
          return !Number.isNaN(Date.parse(value));
        }
        return false;

      case 'Choice':
        if (typeof value !== 'string') {
          return false;
        }
        if (!subField.options || !Array.isArray(subField.options)) {
          return true;
        }
        return subField.options.some(option => option.value === value);

      case 'Lookup':
        return true; // Lookup fields are generally flexible

      default:
        return true; // For unknown types, accept any value
    }
  }
}
